import { useEffect, useRef, useState } from "react";
import api from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Switch } from "../ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { toast } from "sonner";
import { Image as ImageIcon, Upload, Eye, Save, Loader2, Sparkles, CalendarClock, Gift, Power } from "lucide-react";

export default function PromoSettingsPanel() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [cfg, setCfg] = useState({
    enabled: false,
    image_url: "/promo-flash-welcome.jpg",
    cta_url: "/register",
    audience: "new_visitors",
    expires_at: "",
    headline: "",
    version: 1,
  });
  const fileRef = useRef(null);

  const fetchConfig = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/promo/splash");
      setCfg({
        enabled: data.enabled,
        image_url: data.image_url,
        cta_url: data.cta_url,
        audience: data.audience,
        expires_at: (data.expires_at || "").substring(0, 16), // for datetime-local input
        headline: data.headline || "",
        version: data.version,
      });
    } catch (e) {
      toast.error("Impossible de charger la configuration");
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchConfig(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/promo/splash", {
        enabled: cfg.enabled,
        cta_url: cfg.cta_url || "/register",
        audience: cfg.audience,
        expires_at: cfg.expires_at ? new Date(cfg.expires_at).toISOString() : "",
        headline: cfg.headline || "",
      });
      toast.success("Configuration enregistrée");
      fetchConfig();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally { setSaving(false); }
  };

  const handleUpload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/admin/promo/splash/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Image mise à jour");
      setCfg((c) => ({ ...c, image_url: data.url }));
      fetchConfig();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Echec upload");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-[#CBD5E1] p-8 flex justify-center" data-testid="promo-settings-panel">
        <Loader2 className="w-5 h-5 animate-spin text-[#0A2540]" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="promo-settings-panel">
      <div className="bg-white rounded-xl border border-[#CBD5E1] p-5">
        <div className="flex items-center gap-2 mb-4">
          <Sparkles className="w-4 h-4 text-[#FACC15]" />
          <h3 className="text-sm font-bold text-[#0A2540] uppercase tracking-wide" style={{ fontFamily: 'Outfit' }}>
            Splash promo · Page d&apos;accueil
          </h3>
        </div>

        <div className="grid md:grid-cols-2 gap-5">
          {/* Live preview */}
          <div>
            <Label className="text-xs font-medium text-[#0A2540]">Aperçu en direct</Label>
            <div className="mt-1 relative rounded-2xl overflow-hidden border border-[#CBD5E1] bg-[#F8FAFC]" data-testid="promo-preview">
              <img
                src={cfg.image_url || "/promo-flash-welcome.jpg"}
                alt="Aperçu promo"
                className="w-full h-auto block max-h-96 object-contain bg-white"
              />
              <div className="p-2 bg-gradient-to-r from-[#0A2540] to-[#0E2E50] flex items-center justify-between text-white text-[11px]">
                <span><strong className="text-[#FACC15]">Cliquez ici</strong> pour activer</span>
                <span className="px-2 py-0.5 rounded-full bg-[#FACC15] text-[#0A2540] font-bold text-[10px]">CTA → {cfg.cta_url || "/register"}</span>
              </div>
            </div>
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleUpload} data-testid="promo-image-input" />
            <Button
              type="button" size="sm" variant="outline" disabled={uploading}
              onClick={() => fileRef.current?.click()}
              className="mt-2 w-full border-[#CBD5E1] text-[#0A2540]" data-testid="promo-upload-btn"
            >
              {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Upload className="w-3.5 h-3.5 mr-1" />}
              Remplacer l&apos;image (max 5 Mo)
            </Button>
          </div>

          {/* Settings form */}
          <div className="space-y-3">
            <div className="flex items-center justify-between p-3 rounded-lg bg-[#F8FAFC] border border-[#CBD5E1]">
              <div>
                <p className="text-sm font-semibold text-[#0A2540]">Splash actif</p>
                <p className="text-[11px] text-[#64748B]">Affichage automatique sur la page d&apos;accueil</p>
              </div>
              <Switch
                checked={cfg.enabled}
                onCheckedChange={(v) => setCfg({ ...cfg, enabled: v })}
                data-testid="promo-enabled-switch"
              />
            </div>
            <div>
              <Label className="text-xs font-medium text-[#0A2540]">Titre (accessibilité)</Label>
              <Input
                value={cfg.headline}
                onChange={(e) => setCfg({ ...cfg, headline: e.target.value })}
                placeholder="Ex : Offre de bienvenue -15%"
                className="h-9 border-[#CBD5E1] mt-1" data-testid="promo-headline"
              />
            </div>
            <div>
              <Label className="text-xs font-medium text-[#0A2540]">URL au clic (CTA)</Label>
              <Input
                value={cfg.cta_url}
                onChange={(e) => setCfg({ ...cfg, cta_url: e.target.value })}
                placeholder="/register ou https://..."
                className="h-9 border-[#CBD5E1] mt-1" data-testid="promo-cta-url"
              />
            </div>
            <div>
              <Label className="text-xs font-medium text-[#0A2540]">Audience cible</Label>
              <Select value={cfg.audience} onValueChange={(v) => setCfg({ ...cfg, audience: v })}>
                <SelectTrigger className="h-9 border-[#CBD5E1] mt-1" data-testid="promo-audience">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="new_visitors">Nouveaux visiteurs (non connectés)</SelectItem>
                  <SelectItem value="all">Tout le monde (incluant connectés)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-medium text-[#0A2540] flex items-center gap-1">
                <CalendarClock className="w-3 h-3" /> Date de fin (optionnel)
              </Label>
              <Input
                type="datetime-local"
                value={cfg.expires_at}
                onChange={(e) => setCfg({ ...cfg, expires_at: e.target.value })}
                className="h-9 border-[#CBD5E1] mt-1" data-testid="promo-expires-at"
              />
              <p className="text-[10px] text-[#64748B] mt-1">Laisser vide pour ne pas expirer automatiquement</p>
            </div>
            <div className="flex items-center justify-between pt-2">
              <span className="text-[10px] text-[#64748B]">Version : <strong>v{cfg.version}</strong> · Modifier l&apos;image ou le formulaire incrémente la version et re-déclenche le splash pour tous les visiteurs.</span>
            </div>
            <Button
              onClick={save} disabled={saving}
              className="w-full bg-[#0A2540] text-white hover:bg-[#081b2e] font-semibold" data-testid="promo-save-btn"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}
              Enregistrer la configuration
            </Button>
          </div>
        </div>
      </div>

      {/* Help block */}
      <div className="bg-[#FEFCE8] border border-[#FACC15]/40 rounded-xl p-4 text-xs text-[#854D0E]" data-testid="promo-help">
        <p className="font-semibold mb-1 flex items-center gap-1"><Eye className="w-3.5 h-3.5" /> Comment ça marche ?</p>
        <ul className="space-y-1 list-disc list-inside">
          <li>Le splash s&apos;affiche <strong>une seule fois par navigateur</strong>, après ~650 ms sur la page d&apos;accueil</li>
          <li>Quand vous modifiez l&apos;image ou les paramètres, la <strong>version</strong> est incrémentée et tous les visiteurs revoient le splash une fois</li>
          <li>Choisissez <strong>« Nouveaux visiteurs »</strong> pour ne pas déranger vos utilisateurs déjà connectés</li>
          <li>La date de fin masque automatiquement le splash après l&apos;échéance (côté serveur)</li>
        </ul>
      </div>

      {/* Welcome Promo (-15% first booking) global kill-switch */}
      <WelcomePromoConfig />
    </div>
  );
}


function WelcomePromoConfig() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [wp, setWp] = useState({ enabled: true, ends_at: "" });

  const fetchWp = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/promo/welcome");
      setWp({
        enabled: data.enabled !== false,
        ends_at: (data.ends_at || "").substring(0, 16), // for datetime-local
      });
    } catch (e) {
      toast.error("Impossible de charger la promo Welcome");
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchWp(); }, []);

  const saveWp = async () => {
    setSaving(true);
    try {
      await api.put("/admin/promo/welcome", {
        enabled: wp.enabled,
        ends_at: wp.ends_at ? new Date(wp.ends_at).toISOString() : "",
      });
      toast.success(wp.enabled ? "Promo Welcome activée" : "Promo Welcome désactivée");
      fetchWp();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally { setSaving(false); }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-[#CBD5E1] p-8 flex justify-center" data-testid="welcome-promo-config-loading">
        <Loader2 className="w-5 h-5 animate-spin text-[#0A2540]" />
      </div>
    );
  }

  const endsPast = wp.ends_at && new Date(wp.ends_at) < new Date();
  const effectivelyDisabled = !wp.enabled || endsPast;

  return (
    <div className="bg-white rounded-xl border border-[#CBD5E1] p-5" data-testid="welcome-promo-config">
      <div className="flex items-center gap-2 mb-4">
        <Gift className="w-4 h-4 text-[#FACC15]" />
        <h3 className="text-sm font-bold text-[#0A2540] uppercase tracking-wide" style={{ fontFamily: "Outfit" }}>
          Promo Bienvenue · -15% première location
        </h3>
      </div>

      <div className="space-y-3">
        <div className="flex items-center justify-between p-3 rounded-lg bg-[#F8FAFC] border border-[#CBD5E1]">
          <div>
            <p className="text-sm font-semibold text-[#0A2540] flex items-center gap-1.5">
              <Power className={`w-3.5 h-3.5 ${wp.enabled ? "text-[#10B981]" : "text-[#EF4444]"}`} />
              Promo Welcome active
            </p>
            <p className="text-[11px] text-[#64748B]">
              Si désactivée, les nouveaux inscrits ne recevront plus le -15% et la remise ne s&apos;applique plus aux paniers existants.
            </p>
          </div>
          <Switch
            checked={wp.enabled}
            onCheckedChange={(v) => setWp({ ...wp, enabled: v })}
            data-testid="welcome-promo-enabled-switch"
          />
        </div>

        <div>
          <Label className="text-xs font-medium text-[#0A2540] flex items-center gap-1">
            <CalendarClock className="w-3 h-3" /> Date de fin globale (optionnel)
          </Label>
          <Input
            type="datetime-local"
            value={wp.ends_at}
            onChange={(e) => setWp({ ...wp, ends_at: e.target.value })}
            className="h-9 border-[#CBD5E1] mt-1" data-testid="welcome-promo-ends-at"
          />
          <p className="text-[10px] text-[#64748B] mt-1">
            Après cette date, la promo est automatiquement stoppée pour <strong>tous les utilisateurs</strong> (même ceux dont la fenêtre 30 j n&apos;est pas encore terminée).
          </p>
        </div>

        {effectivelyDisabled && (
          <div className="p-2.5 rounded-lg bg-[#FEE2E2] border border-[#EF4444]/30 text-xs text-[#B91C1C]" data-testid="welcome-promo-status-off">
            <strong>Promo actuellement stoppée</strong> — aucun -15% ne sera accordé, ni sur les nouveaux inscrits, ni sur les paniers.
          </div>
        )}
        {!effectivelyDisabled && (
          <div className="p-2.5 rounded-lg bg-[#DCFCE7] border border-[#10B981]/40 text-xs text-[#166534]" data-testid="welcome-promo-status-on">
            <strong>Promo active</strong> — les nouveaux inscrits reçoivent -15% pendant 30 jours sur leur 1<sup>ère</sup> location ≥ 2h.
          </div>
        )}

        <Button
          onClick={saveWp} disabled={saving}
          className="w-full bg-[#0A2540] text-white hover:bg-[#081b2e] font-semibold"
          data-testid="welcome-promo-save-btn"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}
          Enregistrer
        </Button>
      </div>
    </div>
  );
}
